import './StreamingOptimizations.scss';
import { useEffect, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { MdAllOut, MdAttachMoney, MdNotInterested } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import {
  OPTIMIZATION_TYPE_BY_ID,
  SaveStreamingConstraintsRequest,
  StreamingConstraintData,
  StreamingConstraintRule,
  StreamingConstraintSet,
  ValidateStreamingConstraintsRequest
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { ConstraintSaveBar } from '@Components/ConstraintSaveBar/ConstraintSaveBar';
import { CONSTRAINT_WARNING_HEADER } from '@Components/ConstraintWarning/constraint-warning-constants';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import {
  CustomModalEditWidgetHeader,
  SaveChangesInput,
  SelectorOption
} from '@Components/ModalEditWidget/modal-edit-constants';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useSetError } from '../../redux/modals';
import { StreamingOptimizationsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { useCurrencyInputDisplay } from '../../utils/hooks/useCurrencyInputDisplay';
import { useConstraintDraft } from '../../utils/hooks/useConstraintDraft';
import { parseWholeUsd } from '../../utils/text-utils';
import {
  applyTableChanges,
  formatOptimizationDate,
  withIndexes
} from '../../utils/optimization-utils';
import {
  StreamingOptimizationPageProps,
  StreamingPlacementAndDerivedNetwork
} from './streaming-optimizations-constants';
import { getOptimizationTitle } from './streaming-optimizations-utils';
import { getBudgetInputError, getGlobalInputError } from './constraint-validation-utils';

interface EditorConstraintData extends StreamingConstraintData {
  streamingType: string;
}

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

const validateConstraint = async (data: EditorConstraintData): Promise<EditorConstraintData> => ({
  ...data,
  constraints: await awaitJSON<StreamingConstraintSet>(
    await StreamingOptimizationsLambdaFetch('/validate_constraints', {
      method: 'POST',
      headers: jsonHeaders,
      body: data satisfies ValidateStreamingConstraintsRequest
    })
  )
});

const GLOBAL_FIELD_DEFINITIONS = [
  { name: 'Max per [Network + Content] Spend %', key: 'networkMaxSpendPct' },
  { name: 'Unseen Max Spend %', key: 'unseenMaxSpendPct' },
  { name: 'Max Extrapolation Ratio', key: 'maxExtrapolationRatio' }
] as const;

interface GlobalConstraintRow {
  index: number;
  name: string;
  key: (typeof GLOBAL_FIELD_DEFINITIONS)[number]['key'];
  value: number | undefined;
}

const withEditorMetadata = (
  data: StreamingConstraintData,
  streamingType: string
): EditorConstraintData => ({
  ...data,
  optimizationType:
    OPTIMIZATION_TYPE_BY_ID[data.optimization_type_id] ?? data.optimizationType ?? 'buying',
  streamingType,
  constraints: {
    ...data.constraints,
    budget: withIndexes(data.constraints.budget),
    avoid: withIndexes(data.constraints.avoid),
    creativesAllowed: withIndexes(data.constraints.creativesAllowed),
    networkCreativesDisallowed: withIndexes(data.constraints.networkCreativesDisallowed)
  }
});

const persistConstraint = async (data: EditorConstraintData): Promise<EditorConstraintData> =>
  withEditorMetadata(
    await awaitJSON<StreamingConstraintData>(
      await StreamingOptimizationsLambdaFetch('/constraints', {
        method: 'POST',
        headers: jsonHeaders,
        body: data satisfies SaveStreamingConstraintsRequest
      })
    ),
    data.streamingType
  );

const uniqueOptions = (
  placements: StreamingPlacementAndDerivedNetwork[],
  field: keyof StreamingPlacementAndDerivedNetwork
): SelectorOption[] =>
  [...new Set(placements.flatMap(row => (row[field] == null ? [] : [String(row[field])])))]
    .sort()
    .map(value => ({ label: value, value }));

const hasValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '' && value !== false && value !== 0;
};

const checkBudgetValid = (
  row: StreamingConstraintRule,
  invalid?: (message: string) => void
): boolean => {
  if (!row.name) {
    invalid?.('Constraint must be named');
    return false;
  }
  if (![row.minValue, row.maxValue, row.minPct, row.maxPct].some(value => value != null)) {
    invalid?.('Must specify at least one min or max field');
    return false;
  }
  if (![row.placements, row.publishers, row.format].some(hasValue)) {
    invalid?.('Must specify at least one constraint field');
    return false;
  }
  const numericError = getBudgetInputError(row);
  if (numericError) {
    invalid?.(numericError);
    return false;
  }
  return true;
};

const checkAvoidValid = (
  row: StreamingConstraintRule,
  invalid?: (message: string) => void
): boolean => {
  if (![row.placements, row.publishers, row.format].some(hasValue)) {
    invalid?.('Must specify at least one constraint field');
    return false;
  }
  return true;
};

const checkGlobalValid = (
  row: Partial<GlobalConstraintRow>,
  invalid?: (message: string) => void
): boolean => {
  const error = getGlobalInputError(row);
  if (error) {
    invalid?.(error);
    return false;
  }
  return true;
};

const budgetHeaders: CustomModalEditWidgetHeader[] = [
  CONSTRAINT_WARNING_HEADER,
  { type: 'text', field: 'name', label: 'Name', flex: 1, modalRow: 0, modalFlex: 1 },
  {
    type: 'select',
    field: 'placements',
    label: 'Network + Content',
    isMulti: true,
    options: 'placements',
    modalRow: 2,
    modalFlex: 1
  },
  {
    type: 'select',
    field: 'publishers',
    label: 'Publishers',
    isMulti: true,
    options: 'publishers',
    modalRow: 2,
    modalWidth: 200
  },
  {
    type: 'select',
    field: 'format',
    label: 'Format',
    options: 'format',
    modalRow: 2,
    modalWidth: 150
  },
  {
    type: 'currency',
    field: 'minValue',
    omitEmptyNumber: true,
    label: 'Min $',
    width: 100,
    modalRow: 3,
    modalFlex: 1
  },
  {
    type: 'percent',
    field: 'minPct',
    omitEmptyNumber: true,
    label: 'Min %',
    width: 100,
    modalRow: 3,
    modalFlex: 1
  },
  {
    type: 'currency',
    field: 'maxValue',
    omitEmptyNumber: true,
    label: 'Max $',
    width: 100,
    modalRow: 3,
    modalFlex: 1
  },
  {
    type: 'percent',
    field: 'maxPct',
    omitEmptyNumber: true,
    label: 'Max %',
    width: 100,
    modalRow: 3,
    modalFlex: 1
  }
];

const avoidHeaders: CustomModalEditWidgetHeader[] = [
  CONSTRAINT_WARNING_HEADER,
  {
    type: 'select',
    field: 'placements',
    label: 'Network + Content',
    isMulti: true,
    options: 'placements',
    flex: 1,
    modalRow: 1,
    modalFlex: 1
  },
  {
    type: 'select',
    field: 'publishers',
    label: 'Publishers',
    isMulti: true,
    options: 'publishers',
    modalRow: 2,
    modalFlex: 1
  },
  {
    type: 'select',
    field: 'format',
    label: 'Format',
    options: 'format',
    modalRow: 2,
    modalWidth: 250
  }
];

const globalHeaders: CustomModalEditWidgetHeader[] = [
  {
    label: 'Name',
    field: 'name',
    flex: 1,
    minFlexWidth: 260,
    readOnly: true,
    modalRow: 0,
    modalFlex: 4
  },
  {
    label: 'Value',
    field: 'value',
    type: 'decimal',
    omitEmptyNumber: true,
    modalRow: 0,
    modalFlex: 1,
    renderer: (row: GlobalConstraintRow) => (
      <NumberFormatter value={row.value} type={row.key.includes('Pct') ? '%' : '#'} />
    ),
    rendererType: (row: GlobalConstraintRow) => (row.key.includes('Pct') ? 'percent' : 'decimal')
  }
];

export const ConstraintView = ({ company, streamingType }: StreamingOptimizationPageProps) => {
  const title = getOptimizationTitle(streamingType);
  const { constraintId = '' } = useParams();
  const setError = useSetError();
  const [initialData, setInitialData] = useState<EditorConstraintData>();
  const {
    data,
    update,
    discard,
    save,
    changeCount,
    canSave,
    warnings: validationWarnings,
    saving,
    validating,
    validationError,
    retryValidation
  } = useConstraintDraft({
    identity: `${company}/${streamingType}/${constraintId}`,
    initialData:
      initialData?.company === company &&
      initialData?.name === constraintId &&
      initialData?.streamingType === streamingType
        ? initialData
        : undefined,
    validate: validateConstraint,
    persist: persistConstraint
  });
  const [placements, setPlacements] = useState<StreamingPlacementAndDerivedNetwork[]>([]);
  const [loadingError, setLoadingError] = useState<string>();

  useEffect(() => {
    if (!company || !constraintId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const raw = await awaitJSON<StreamingConstraintData>(
          await StreamingOptimizationsLambdaFetch('/constraints', {
            params: { company, name: constraintId, streamingType }
          })
        );
        if (!cancelled) {
          setInitialData(withEditorMetadata(raw, streamingType));
          setLoadingError(undefined);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : String(error);
          setLoadingError(message);
          setError({
            message: `Failed to fetch constraint "${constraintId}". ${message}`,
            reportError: error
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, constraintId, setError, streamingType]);

  useEffect(() => {
    if (!company) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await awaitJSON<StreamingPlacementAndDerivedNetwork[]>(
          await StreamingOptimizationsLambdaFetch('/getPlacementsAndDerivedNetworks', {
            params: { company, mediaType: streamingType }
          })
        );
        if (!cancelled) {
          setPlacements(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to receive placement data. ${error instanceof Error ? error.message : String(error)}`,
            reportError: error
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, setError, streamingType]);

  const selectorOptions = {
    placements: uniqueOptions(placements, 'placement'),
    publishers: uniqueOptions(placements, 'publisher'),
    format: uniqueOptions(placements, 'format')
  };

  const applyTable = (
    section: 'budget' | 'avoid',
    changes: SaveChangesInput<StreamingConstraintRule>
  ): void => {
    if (!data) return;
    const rows = applyTableChanges(
      data.constraints[section],
      changes.addedData,
      changes.modifiedData,
      changes.deletedData
    );
    update({
      ...data,
      constraints: { ...data.constraints, [section]: withIndexes(rows) }
    });
  };

  const globalRows: GlobalConstraintRow[] = data
    ? GLOBAL_FIELD_DEFINITIONS.map(({ name, key }, index) => ({
        index,
        name,
        key,
        value: data.constraints.global[key]
      }))
    : [];

  const budgetInput = useCurrencyInputDisplay(data?.constraints.total ?? 0);

  if (loadingError) {
    return (
      <Page title={title} pageType={title}>
        <div className='optimization-inline-error'>Failed to load constraint: {loadingError}</div>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page title={title} pageType={title}>
        <FullPageSpinner />
      </Page>
    );
  }

  const isTraffic = data.optimizationType === 'traffic';
  const invalidBudget =
    !Number.isFinite(data.constraints.total) || (data.constraints.total ?? 0) <= 0;
  const formattedLastModified = formatOptimizationDate(data.lastmodified);
  const lastModified =
    formattedLastModified === data.lastmodified
      ? data.lastmodified
      : `${(data.lastuser ?? '').replace(/@.*/, '')} at ${formattedLastModified}`;

  return (
    <Page
      title={
        <span className='optimization-run-title'>
          <Link
            className='optimization-title-link'
            to={`/${company}/${streamingType}/optimizations`}
          >
            {title}
          </Link>
          <span aria-hidden='true'>/</span>
          <span>{data.name}</span>
        </span>
      }
      pageType={title}
      minHeight='600px'
      subHeader={
        <ConstraintSaveBar
          changeCount={changeCount}
          saving={saving}
          disabled={!canSave || (!isTraffic && invalidBudget)}
          validating={validating}
          validationError={validationError}
          onRetryValidation={retryValidation}
          onSave={save}
          onDiscard={discard}
        >
          {isTraffic ? (
            <div className='constraint-allocation-dates'>
              Allocation: {data.start} – {data.end}
            </div>
          ) : (
            <Form.Group className='constraint-overall-budget'>
              <Form.Label>Overall Budget</Form.Label>
              <Form.Control
                type='text'
                isInvalid={invalidBudget}
                value={budgetInput.displayValue}
                onFocus={budgetInput.onFocus}
                onChange={event => {
                  budgetInput.onChangeText(event.target.value);
                  const value = parseWholeUsd(event.target.value);
                  update({
                    ...data,
                    constraints: { ...data.constraints, total: value }
                  });
                }}
                onBlur={budgetInput.onBlur}
                disabled={saving}
              />
              {invalidBudget && (
                <span className='text-danger' role='alert'>
                  Overall budget must be greater than zero.
                </span>
              )}
            </Form.Group>
          )}
        </ConstraintSaveBar>
      }
      actions={
        <div className='constraint-page-actions'>
          <span className='last-modified'>Last modified {lastModified}</span>
        </div>
      }
    >
      <div className='constraint-view'>
        {validationWarnings.length > 0 && (
          <Alert variant='warning' aria-live='polite' data-testid='constraint-validation-warnings'>
            <Alert.Heading>Resolve these constraints before saving</Alert.Heading>
            <ul className='mb-0'>
              {validationWarnings.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}
        <ModalEditWidget
          className='budget-constraints-widget'
          headers={budgetHeaders}
          name={
            <span className='constraint-widget-title'>
              <MdAttachMoney /> Budget Constraints
            </span>
          }
          initialTableData={data.constraints.budget}
          saveOnEdit
          saveButtonText='Apply'
          readOnly={saving}
          autoHeight
          selectorOptions={selectorOptions}
          checkIsValid={checkBudgetValid}
          onSaveModal={changes => applyTable('budget', changes)}
        />

        <div className='constraint-widget-grid'>
          <ModalEditWidget
            className='avoid-constraints-widget'
            headers={avoidHeaders}
            name={
              <span className='constraint-widget-title'>
                <MdNotInterested /> Avoid Constraints
              </span>
            }
            initialTableData={data.constraints.avoid}
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            autoHeight
            selectorOptions={selectorOptions}
            checkIsValid={checkAvoidValid}
            onSaveModal={changes => applyTable('avoid', changes)}
          />
          {!isTraffic && (
            <ModalEditWidget
              className='global-constraints-widget'
              headers={globalHeaders}
              name={
                <span className='constraint-widget-title'>
                  <MdAllOut /> Global Constraints
                </span>
              }
              initialTableData={globalRows}
              saveOnEdit
              saveButtonText='Apply'
              readOnly={saving}
              autoHeight
              selectorOptions={{}}
              enableAdd={false}
              enableDelete={false}
              checkIsValid={checkGlobalValid}
              footerText='We do not recommend values over 3.0 since you will be allowing extrapolation more than 3x beyond historical maxima.'
              checkShowModalFooter={row => row.key === 'maxExtrapolationRatio'}
              onSaveModal={changes => {
                const rows = applyTableChanges(
                  globalRows,
                  changes.addedData,
                  changes.modifiedData,
                  changes.deletedData
                );
                const global = Object.fromEntries(rows.map(row => [row.key, row.value]));
                update({
                  ...data,
                  constraints: {
                    ...data.constraints,
                    global: { ...data.constraints.global, ...global }
                  }
                });
              }}
            />
          )}
        </div>
      </div>
    </Page>
  );
};
